import { push } from "connected-react-router";
import { fork, put, takeEvery, takeLatest } from "redux-saga/effects";

import { callApi } from "../../../sagas/helpers/api";
import { setError, setSuccess, startLoading } from "../../../store/appSlice";
import * as CashManagementAccountApi from "../api/CashManagement";
import {
  cashManagementOperationPaths,
  createAccount,
  fetchAccount,
  fetchAccounts,
  sendTestSettlementBatch,
  setAccountActiveFlag,
  setItem,
  setList,
  updateAccount,
  updateItem,
} from "./cashManagement.slice";

import type { ObjectWithId } from "../../../utils/ObjectWithId";
import type { CashManagementAccount } from "../models/CashManagement";

/** @deprecated */
function* fetchAccountsSaga({
  payload,
}: {
  payload: {
    id: string;
    active: boolean;
  };
  type: string;
}) {
  const { id, active = undefined } = payload;
  const { listing: operationPath } = cashManagementOperationPaths(id);

  yield put(startLoading(operationPath));

  try {
    // @ts-ignore noImplicitAny
    const data = yield callApi(
      CashManagementAccountApi.getCashManagementAccounts,
      id,
      active
    );

    yield put(setList(data));
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* fetchAccountSaga({
  payload,
}: {
  payload: {
    id: string;
  };
  type: string;
}) {
  const { id } = payload;
  const { details: operationPath } = cashManagementOperationPaths(id);

  yield put(startLoading(operationPath));
  try {
    // @ts-ignore noImplicitAny
    const item = yield callApi(
      CashManagementAccountApi.getCashManagementAccount,
      id
    );

    yield put(setItem({ id, item }));
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* updateAccountSaga({
  payload,
}: {
  payload: { id: string; data: CashManagementAccount };
  type: string;
}) {
  const { id, data } = payload;

  const { update: operationPath } = cashManagementOperationPaths(id);

  yield put(startLoading(operationPath));

  try {
    // @ts-ignore noImplicitAny
    const account = yield callApi(
      CashManagementAccountApi.patchCashManagementAccount,
      id,
      data
    );

    yield put(updateItem({ id, itemPart: account }));
    yield put(setSuccess(operationPath));
    yield put(
      push(
        `/configuration/customers/${account.customer}/cash-management/account/${account.id}`
      )
    );
  } catch (error) {
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* createAccountSaga({
  payload,
}: {
  payload: { id: string; data: CashManagementAccount };
  type: string;
}) {
  const { id, data } = payload;

  const { create: operationPath } = cashManagementOperationPaths(id);

  yield put(startLoading(operationPath));

  try {
    // @ts-ignore noImplicitAny
    const account = yield callApi(
      CashManagementAccountApi.postCashManagementAccount,
      id,
      data
    );

    yield put(setItem({ id, item: account }));
    yield put(setSuccess(operationPath));
    yield put(
      push(
        `/configuration/customers/${id}/cash-management/account/${account.id}`
      )
    );
  } catch (error) {
    yield put(
      setError({
        path: operationPath,
        error: error,
      })
    );
  }
}

function* setAccountActiveFlagSaga({
  payload,
}: {
  payload: { id: string; data: CashManagementAccount };
  type: string;
}) {
  const { id, data } = payload;
  const { setActiveFlag: operationPath } = cashManagementOperationPaths(id);

  yield put(startLoading(operationPath));
  try {
    // @ts-ignore noImplicitAny
    const account = yield callApi(
      CashManagementAccountApi.patchCashManagementAccount,
      id,
      data
    );

    yield put(updateItem({ id, itemPart: { active: account.active } }));
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* sendTestSettlementBatchSaga({
  payload,
}: {
  payload: ObjectWithId;
  type: string;
}) {
  const { id } = payload;
  const {
    sendTestSettlementBatch: operationPath,
  } = cashManagementOperationPaths(id);

  yield put(startLoading(operationPath));

  try {
    yield callApi(CashManagementAccountApi.testCashManagementSettlement, id);
    yield put(setSuccess(operationPath));
  } catch (error) {
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* watchFetchAccounts() {
  yield takeEvery(fetchAccounts.type, fetchAccountsSaga);
}

function* watchFetchAccount() {
  yield takeEvery(fetchAccount.type, fetchAccountSaga);
}

function* watchUpdateAccount() {
  yield takeEvery(updateAccount.type, updateAccountSaga);
}

function* watchCreateAccount() {
  yield takeEvery(createAccount.type, createAccountSaga);
}

function* watchSendTestSettlementBatch() {
  yield takeEvery(sendTestSettlementBatch.type, sendTestSettlementBatchSaga);
}

function* watchSetAccountActiveFlagSaga() {
  yield takeLatest(setAccountActiveFlag.type, setAccountActiveFlagSaga);
}

export default function* rootSaga() {
  yield fork(watchFetchAccounts);
  yield fork(watchFetchAccount);
  yield fork(watchUpdateAccount);
  yield fork(watchCreateAccount);
  yield fork(watchSendTestSettlementBatch);
  yield fork(watchSetAccountActiveFlagSaga);
}
